<script setup lang="ts">
import { EEventActions } from '@/store/eventStore/EventStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { EventModel } from '@/types/models/EventModel'
import {
    BaseDialog,
    DateTimePickerField,
    DividerItem,
    PrimaryButton,
    RadioButton,
    StackLayout,
    StringInputField
} from '@rfc/rfc-components-library'
import type { SelectorObject } from '@rfc/rfc-components-library/types'
import moment from 'moment'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const store = useStore()

type Props = {
    data: EventModel
}
const props = defineProps<Props>()

type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const { t } = useI18n()

const open = ref(false)

const guntimes = ref<{ name?: string; start_at?: Date }[]>([{}])
const guntimeIsNow = ref<boolean>()

const handleConfirm = () =>
    store
        ?.dispatch(`${EStoreModules.EVENT}/${EEventActions.SET_GUNTIME}`, {
            data: {
                guntimes: guntimeIsNow.value
                    ? [{ ...guntimes.value[0], start_at: moment().utc().toDate() }]
                    : guntimes.value.filter(item => item.name && item.start_at)
            },
            eventId: props.data.id
        })
        .then(() => {
            emit('refresh')
            open.value = false
        })
        .finally(() => (guntimes.value = [{}]))

const guntimesOptions = computed<SelectorObject[]>(() => [
    {
        name: t('event.actions.guntime.now'),
        value: 1
    },
    {
        name: t('event.actions.guntime.multipleGuntimes'),
        value: 0
    }
])
</script>

<template>
    <PrimaryButton :label="$t('event.actions.guntime.addGuntimeTitle')" @click="open = true" />
    <BaseDialog
        :title="$t('event.actions.guntime.addGuntimeTitle')"
        :open="open"
        @confirm="handleConfirm"
        @close="open = false"
        class="guntime-component">
        <template #content>
            <StackLayout direction="column" :gap="8">
                <RadioButton
                    :modelValue="guntimeIsNow ? 1 : 0"
                    :options="guntimesOptions"
                    @change="guntimeIsNow = !!$event" />
                <template v-if="!guntimeIsNow">
                    <StackLayout
                        v-for="(item, key) in guntimes"
                        :key="key"
                        :gap="8"
                        direction="column">
                        <StackLayout :gap="8" isResponsive>
                            <StringInputField
                                :label="$t('event.actions.guntime.name')"
                                :modelValue="item?.name"
                                @change="guntimes[key] = { ...guntimes[key], name: $event }"
                                class="w-full" />
                            <DateTimePickerField
                                :label="$t('event.actions.guntime.startAt')"
                                :modelValue="item?.start_at"
                                class="w-full guntime-datepicker"
                                @change="
                                    guntimes[key] = {
                                        ...guntimes[key],
                                        start_at: $event ? moment($event).utc().toDate() : undefined
                                    }
                                " />
                        </StackLayout>
                        <DividerItem v-if="key !== guntimes.length - 1" isPrimary />
                    </StackLayout>
                    <PrimaryButton
                        :label="$t('event.actions.guntime.addGuntime')"
                        @click="guntimes.push({})"
                        class="fit-content" />
                </template>
                <StringInputField
                    v-else
                    :label="$t('event.actions.guntime.name')"
                    :modelValue="guntimes[0]?.name"
                    @change="guntimes[0] = { ...guntimes[0], name: $event }" />
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.guntime-component {
    & :deep(.card-content) {
        position: relative;
        overflow: auto;
    }
}

.guntime-datepicker {
    & :deep(.datepicker) {
        & .dropdown-content {
            position: fixed;
            display: flex;
            flex-flow: wrap;
        }
        & .timepicker {
            & .dropdown-content {
                position: relative;
                flex-flow: unset;
            }
        }
    }
}
</style>
